import React, { useState, useRef, useEffect } from 'react';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { useLocale } from 'locale';
import Footer from 'components/static-footer';
import AutocompleteInput from 'components/autocomplete/single-location-autocomplete';
import { CompletionType, IAutocompleteCity } from 'utils/entities';
import {
  Root,
  MainHeader,
  Content,
  ContentWrapper,
  AgenciesSection,
  AgentsSection,
  SectionHeader,
  CardsCount,
  CardsCountHeader,
  AutocompleteWrapper,
  MobileNavigation,
} from './styled';
import LocationIcon from 'assets/svg/transportation/location-16.svg';
import { MadadAgenciesGrid } from 'components/madad/agencies-grid/AgenciesGrid';
import { MadadAgentsGrid } from 'components/madad/agents-grid/AgentsGrid';
import { AddressAutocompleteSource } from 'components/autocomplete/types';
import { LinkToMadadLanding } from './LinkToMadadLanding';
import { MadadAgentCard, MadadAgencyCard } from 'components/madad/entities';
import { CountTooltip } from './CountTooltip';
import { variantToImagePath } from 'screens/MadadPage/card/consts';


const AUTOCOMPLETE_OPTIONS_COUNT = 6;
const COMPLETION_TYPES = [ CompletionType.City ];
const SCROLL_TO_SETTINGS = { time: 0, align: { top: 0, topOffset: 50 } };

interface MadadSearchPageProps {
  agents: MadadAgentCard[];
  agencies: MadadAgencyCard[];
  localDoc: IAutocompleteCity;
  isLoading: boolean;
  onSearchChange: (value: IAutocompleteCity) => void;
}

export const MadadSearchPage: React.FC<MadadSearchPageProps> = (props) => {
  const { agents, agencies, localDoc, isLoading, onSearchChange } = props;
  const city = localDoc && localDoc.city || '';
  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const agentsScrollToRef = useRef(null);
  const agenciesScrollToRef = useRef(null);
  const [ cityName, setCityName ] = useState(city);
  const [ searchStr, setSearchStr ] = useState('');
  const isMobile = screenBreakpoint === 1;
  const isCity = (localDoc && localDoc.type) === CompletionType.City;

  const handleSearchChange = (value: IAutocompleteCity) => {
    if (value) {
      setCityName(value.name);
      onSearchChange(value);
    }
  };

  useEffect(() => {
    if (city) {
      setCityName(city);
    }
  }, [ city ]);

  const autocompleteInput = (
    <AutocompleteInput
      searchStr={searchStr}
      placeholder=""
      inputIcon={<LocationIcon height={16} width={16} />}
      completionTypes={COMPLETION_TYPES}
      optionsCount={AUTOCOMPLETE_OPTIONS_COUNT}
      source={AddressAutocompleteSource.MadadSearch}
      onSearchStrChange={setSearchStr}
      onChange={handleSearchChange}
    />
  );

  return (
    <Root>
      {isMobile ? (
        <MobileNavigation>
          <LinkToMadadLanding />
          {autocompleteInput}
        </MobileNavigation>
      ) : null}
      <ContentWrapper>
        <Content>
          {isMobile ? null : <LinkToMadadLanding label={t('madadSearch.backButtonTitle')} />}
          <MainHeader>{t('madadSearch.mainHeader', { name: cityName })}</MainHeader>
          {isMobile ? null : <AutocompleteWrapper>{autocompleteInput}</AutocompleteWrapper>}
          <AgenciesSection ref={agenciesScrollToRef}>
            <SectionHeader>{t('madadSearch.agenciesSection.header', { name: cityName })}</SectionHeader>
            <CardsCountHeader>
              <CardsCount>{t('madadSearch.agenciesSection.count', { count: agencies.length })}</CardsCount>
              {isCity && <CountTooltip />}
            </CardsCountHeader>
            <MadadAgenciesGrid
              items={agencies}
              withLoader
              isLoading={isLoading}
              scrollToRef={agenciesScrollToRef}
              scrollToSettings={SCROLL_TO_SETTINGS}
              withPropertyInfo
              variantToImagePath={variantToImagePath}
            />
          </AgenciesSection>
          <AgentsSection ref={agentsScrollToRef}>
            <SectionHeader>{t('madadSearch.agentsSection.header', { name: cityName })}</SectionHeader>
            <CardsCountHeader>
              <CardsCount>{t('madadSearch.agentsSection.count', { count: agents.length })}</CardsCount>
            </CardsCountHeader>
            <MadadAgentsGrid
              items={agents}
              withLoader
              isLoading={isLoading}
              scrollToRef={agentsScrollToRef}
              scrollToSettings={SCROLL_TO_SETTINGS}
              withPropertyInfo
            />
          </AgentsSection>
        </Content>
      </ContentWrapper>
      <Footer />
    </Root>
  );
};
